'use client';
import { useFormatter, useTranslations } from 'next-intl';
import { type FC, useRef, useState } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { CopyIcon } from '@/app/components/icons/CopyIcon';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Button } from '@/app/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/components/ui/table';
import { SITE } from '@/app/constants';
import { useCopy } from '@/app/hooks';
import { cn } from '@/app/utils';
import { PAY_CHANNEL_MAP } from '../../constants';
import type { OrderInfo } from '../../types';
import { InvoiceDialog } from './InvoiceDialog';

const StatusCom = ({ text }: { text: string }) => {
  return (
    <div className="rounded-full bg-[rgba(62,163,255,0.20)] px-[11px] py-[3px] text-[#3EA3FF] text-[10px] leading-[14px]">
      {text}
    </div>
  );
};

export const OrderTable: FC<{ orders: OrderInfo[]; loading: boolean; total: number }> = ({
  orders,
  loading,
  total,
}) => {
  const t = useTranslations();
  const [open, setOpen] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<string>('');
  const format = useFormatter();
  const ref = useRef<HTMLDivElement>(null);

  const { copy } = useCopy();
  const handleCopyOrderId = (orderId: string) => {
    copy(orderId);
  };

  const getOrderName = (order: OrderInfo) => {
    const { product_type, plan_type, billing_type, order_type } = order;
    let name = '';
    if (product_type === 'credits_purchase') {
      name = t('payments.orderList.creditsPurchase');
    } else if (product_type === 'claw_pass' || product_type === 'claw_pass_bundle') {
      if (order.claw_pass_plan_type === 'claw_pass_basic') {
        name = t('payments.claw_pass_basic');
      } else if (order.claw_pass_plan_type === 'claw_pass_pro') {
        name = t('payments.claw_pass_pro');
      }
      // 区分权益包 / 捆绑套餐
      const suffix =
        product_type === 'claw_pass_bundle' ? t('payments.plans.bundleLabel') : t('payments.plans.passLabel');
      name = name ? `${name} - ${suffix}` : suffix;
    } else if (product_type === 'team_bundle') {
      // 企业版：按 tier 区分档位（basic/plus/pro），tier 缺失或 unspecified 时回退为「企业版」，再加年/月订阅后缀
      const { tier } = order;
      name =
        tier === 'basic' || tier === 'plus' || tier === 'pro'
          ? t(`payments.plans.tier${tier.charAt(0).toUpperCase()}${tier.slice(1)}` as 'payments.plans.tierBasic')
          : t('payments.mySubscription.teamEdition');
      // 与套餐页 Tab（billingYearly / billingQuarterly）口径一致
      if (billing_type === 'yearly') {
        name = `${name}-${t('payments.plans.billingYearly')}`;
      } else if (billing_type === 'quarterly') {
        name = `${name}-${t('payments.plans.billingQuarterly')}`;
      }
    } else {
      if (plan_type === 'standard_v2') {
        name = t('payments.standard_v2');
      } else if (plan_type === 'premium_v2') {
        name = t('payments.premium_v2');
      } else if (plan_type === 'ultimate_v2') {
        name = t('payments.ultimate_v2');
      }
      if (billing_type === 'monthly' && product_type === 'subscription') {
        name = `${name}-${t('payments.orderList.subscriptionMonth')}`;
      } else if (billing_type === 'monthly' && product_type === 'pass') {
        name = `${name}-${t('payments.orderList.monthPeriod')}`;
      } else if (billing_type === 'yearly' && product_type === 'subscription') {
        name = `${name}-${t('payments.orderList.subscriptionYear')}`;
      } else if (billing_type === 'yearly' && product_type === 'pass') {
        name = `${name}-${t('payments.orderList.yearPeriod')}`;
      }
    }

    if (order_type === 'paid_trial_pay') {
      name = `${name}-${t('payments.orderList.paidTrialPay')}`;
    }

    return name;
  };

  return (
    <>
      <Table className="px-6 text-system-white">
        <TableHeader className="font-semibold text-[17px] capitalize [&_tr]:border-system-white12">
          <TableRow>
            <TableHead className="min-w-[180px] text-left">{t('payments.orderList.subscription')}</TableHead>
            <TableHead className="min-w-[140px] text-left">{t('payments.orderList.purchaseTime')}</TableHead>
            <TableHead className="min-w-[80px] text-center">{t('payments.orderList.effectiveTime')}</TableHead>
            <TableHead className="min-w-[80px] text-center">{t('payments.orderList.expirationTime')}</TableHead>
            <TableHead className="min-w-[100px] text-center">{t('payments.orderList.orderid')}</TableHead>
            <TableHead className="min-w-[100px] text-center">{t('payments.orderList.paymentMethod')}</TableHead>
            <TableHead className={cn('min-w-[60px]', SITE === 'china' ? 'text-center' : 'text-right')}>
              {t('payments.orderList.amount')}
            </TableHead>
            {SITE === 'china' && (
              <TableHead className="min-w-[90px] text-center">{t('payments.orderList.operation')}</TableHead>
            )}
          </TableRow>
        </TableHeader>

        {loading ? (
          <TableBody>
            <TableRow className="h-[400px]">
              <TableCell colSpan={8} className="text-center">
                <Loading className="mx-auto size-10 animate-spin" />
              </TableCell>
            </TableRow>
          </TableBody>
        ) : total === 0 ? (
          <TableRow className="h-[400px]">
            <TableCell colSpan={8} className="text-center">
              <div className="text-sm text-system-white36">{t('payments.credits.noRecord')}</div>
            </TableCell>
          </TableRow>
        ) : (
          <TableBody className="min-h-[400px] font-normal text-sm">
            {orders.map((order) => {
              return (
                <TableRow key={order.id}>
                  <TableCell className="h-12 text-left">
                    <div className="flex items-center gap-2">
                      {getOrderName(order)}
                      {order.active && <StatusCom text={t('payments.orderList.active')} />}
                      {!order.active && order.period_start && new Date(order.period_start) > new Date() && (
                        <StatusCom text={t('payments.orderList.pending')} />
                      )}
                    </div>
                  </TableCell>
                  {/* 购买时间 */}
                  <TableCell className="text-left">
                    {format.dateTime(new Date(order.created_at), {
                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      second: 'numeric',
                    })}
                  </TableCell>
                  {/* 生效时间 */}
                  <TableCell className="text-center">
                    {order.period_start
                      ? format.dateTime(new Date(order.period_start), {
                          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        })
                      : '-'}
                  </TableCell>
                  {/* 到期时间 */}
                  <TableCell className="text-center">
                    {order.period_end
                      ? format.dateTime(new Date(order.period_end), {
                          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        })
                      : '-'}
                  </TableCell>
                  {/* 订单编号 */}
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      {order.id}
                      <CopyIcon className="h-4 w-4 cursor-pointer" onClick={() => handleCopyOrderId(order.id)} />
                    </div>
                  </TableCell>
                  {/* 支付方式 */}
                  <TableCell className="text-center">{PAY_CHANNEL_MAP[order.pay_channel]}</TableCell>
                  {/* 金额 */}
                  <TableCell className={cn(SITE === 'china' ? 'text-center' : 'text-right')}>
                    {format.number(order.amount / 100, {
                      style: 'currency',
                      currency: order.currency,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  {/* 操作 */}
                  {SITE === 'china' && (
                    <TableCell className="text-center font-normal text-xs">
                      {order.pay_channel === 'apple' ? (
                        <div className="flex items-center justify-center gap-1">
                          已开票
                          <CustomTooltip
                            content={'您的收据已发送至您的Apple邮箱'}
                            disableHoverableContent={true}
                            container={ref.current}
                          >
                            <BaseLineInfo className="inline size-[14px] shrink-0 align-text-bottom" />
                          </CustomTooltip>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          className="h-8 w-[80px] border-none py-[6px] disabled:bg-[#141414] disabled:opacity-100"
                          disabled={order.invoice_state !== 'not_issue'}
                          onClick={() => {
                            if (order.invoice_state === 'not_issue') {
                              setOrderId(order.id);
                              setOpen(true);
                            }
                          }}
                        >
                          {order.invoice_state === 'not_issue'
                            ? '开发票'
                            : order.invoice_state === 'issuing'
                              ? '开票中'
                              : order.invoice_state === 'issue_failure'
                                ? '开票失败'
                                : '已开票'}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
      <InvoiceDialog open={open} onOpenChange={setOpen} orderId={orderId} />
    </>
  );
};
